/**
 * BillingDashboard — admin overview of money flowing in.
 *
 * KPI strip + aging buckets + by-room revenue table + pending-review +
 * overdue panels. Reuses the existing AdminHeader / KpiTile patterns —
 * NO new visual language.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle2,
  Building2,
  ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

function money(amount, currency = "AED") {
  return `${currency} ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function StatusPill({ status }) {
  const { t } = useI18n();
  const palette = {
    paid: "bg-[#E6EFE8] text-[#1F3A2A]",
    issued: "bg-[#F4E5C5] text-[#8C6D1F]",
    pending: "bg-[#FDE8C5] text-[#8C4A1F]",
    partial: "bg-[#F4E5C5] text-[#8C6D1F]",
    overdue: "bg-[#FCE2E2] text-[#A11D1D]",
    refunded: "bg-[#EEF0F4] text-[#6B7280]",
    adjusted: "bg-[#EEF0F4] text-[#6B7280]",
    void: "bg-[#EEF0F4] text-[#9CA3AF] line-through",
    draft: "bg-[#EEF0F4] text-[#6B7280]",
  };
  return (
    <span
      className={`inline-block text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${palette[status] || palette.draft}`}
      data-testid={`billing-invoice-status-pill-${status}`}
    >
      {t(`billing.status.${status}`) || status}
    </span>
  );
}

function KpiTile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#EEF0F4] bg-white p-5 flex items-start gap-3"
    >
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: accent + "22", color: accent }}
      >
        <Icon size={18} strokeWidth={1.6} />
      </span>
      <div>
        <div className="eyebrow">{label}</div>
        <div className="mt-1.5 font-display text-2xl text-[#1A1B2E] tabular-nums">
          {value}
        </div>
      </div>
    </div>
  );
}

export default function BillingDashboard() {
  const { t } = useI18n();
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, inv] = await Promise.all([
          api.get("/admin/billing/dashboard"),
          api.get("/admin/billing/invoices"),
        ]);
        setDashboard(d.data);
        setInvoices(inv.data || []);
      } catch (e) {
        // calm fall-through
      }
      setLoading(false);
    })();
  }, []);

  const pendingReview = useMemo(
    () => invoices.filter((i) => i.status === "pending"),
    [invoices]
  );
  const overdue = useMemo(
    () => invoices.filter((i) => i.status === "overdue"),
    [invoices]
  );

  if (loading) {
    return (
      <main className="p-6 lg:p-10">
        <p className="font-serif italic text-[#525252]">{t("billing.common.loading")}</p>
      </main>
    );
  }

  const currency = dashboard?.currency || "AED";

  return (
    <main className="p-6 lg:p-10 space-y-8" data-testid="admin-billing-dashboard">
      <AdminHeader
        eyebrow={t("billing.dashboard.eyebrow")}
        title={
          <>
            {t("billing.dashboard.title_lead")}{" "}
            <em className="font-serif italic text-[#1F3A2A]">
              {t("billing.dashboard.title_italic")}
            </em>
          </>
        }
        action={
          <Link
            to="/admin/billing/invoices"
            data-testid="admin-billing-go-invoices"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            {t("billing.invoices.eyebrow")}
            <ArrowRight size={14} strokeWidth={1.8} />
          </Link>
        }
      />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiTile
          icon={Wallet}
          label={t("billing.dashboard.kpi_outstanding")}
          value={money(dashboard.outstanding_balance, currency)}
          accent="#1F3A2A"
          testid="kpi-outstanding"
        />
        <KpiTile
          icon={CheckCircle2}
          label={t("billing.dashboard.kpi_collected_mtd")}
          value={money(dashboard.collected_mtd, currency)}
          accent="#1F8A3F"
          testid="kpi-collected"
        />
        <KpiTile
          icon={TrendingUp}
          label={t("billing.dashboard.kpi_invoiced_mtd")}
          value={money(dashboard.invoiced_mtd, currency)}
          accent="#8C6D1F"
          testid="kpi-invoiced"
        />
        <KpiTile
          icon={AlertCircle}
          label={t("billing.dashboard.kpi_overdue")}
          value={`${dashboard.overdue_count} · ${money(dashboard.overdue_total, currency)}`}
          accent="#A11D1D"
          testid="kpi-overdue"
        />
        <KpiTile
          icon={Clock}
          label={t("billing.dashboard.kpi_pending_review")}
          value={`${dashboard.pending_review_count} · ${money(dashboard.pending_review_total, currency)}`}
          accent="#8C4A1F"
          testid="kpi-pending"
        />
        <KpiTile
          icon={Building2}
          label={t("billing.dashboard.kpi_subsidy")}
          value={money(dashboard.subsidy_outstanding, currency)}
          accent="#3D5C46"
          testid="kpi-subsidy"
        />
      </section>

      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6 lg:p-8"
        data-testid="billing-aging-section"
      >
        <h3 className="font-display text-xl text-[#1A1B2E]">
          {t("billing.dashboard.aging_title")}
        </h3>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(dashboard.aging || []).map((b) => (
            <div
              key={b.label}
              data-testid={`aging-bucket-${b.label}`}
              className="rounded-2xl border border-[#EEF0F4] p-4 bg-[#FAF9F1]/60"
            >
              <div className="eyebrow">{b.label}</div>
              <div className="mt-1 text-sm text-[#1A1B2E] font-medium">
                {b.count} {t("billing.invoices.eyebrow").toLowerCase()}
              </div>
              <div className="mt-1 font-display text-lg text-[#1A1B2E] tabular-nums">
                {money(b.total, currency)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6 lg:p-8"
        data-testid="billing-by-room-section"
      >
        <h3 className="font-display text-xl text-[#1A1B2E]">
          {t("billing.dashboard.by_room_title")}
        </h3>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px]">
              <tr>
                <th className="py-2 pr-4">Room</th>
                <th className="py-2 pr-4">{t("billing.dashboard.by_room_invoiced")}</th>
                <th className="py-2 pr-4">{t("billing.dashboard.by_room_collected")}</th>
                <th className="py-2 pr-4">{t("billing.dashboard.by_room_outstanding")}</th>
                <th className="py-2">{t("billing.dashboard.by_room_families")}</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard.by_room || []).map((r) => (
                <tr
                  key={r.room_id || r.room_name}
                  className="border-t border-[#EEF0F4]"
                  data-testid={`by-room-row-${r.room_id || "no_room"}`}
                >
                  <td className="py-3 pr-4 font-medium text-[#1A1B2E]">{r.room_name}</td>
                  <td className="py-3 pr-4 tabular-nums">{money(r.invoiced, currency)}</td>
                  <td className="py-3 pr-4 tabular-nums text-[#1F8A3F]">
                    {money(r.collected, currency)}
                  </td>
                  <td className="py-3 pr-4 tabular-nums">{money(r.outstanding, currency)}</td>
                  <td className="py-3 text-[#6B7280]">{r.family_count}</td>
                </tr>
              ))}
              {(!dashboard.by_room || dashboard.by_room.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-[#9CA3AF] italic">
                    No billing data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {pendingReview.length > 0 && (
        <section
          className="rounded-3xl bg-[#FDF8E5] border border-[#F4E5C5] p-6 lg:p-8"
          data-testid="billing-pending-review-section"
        >
          <h3 className="font-display text-xl text-[#8C4A1F]">
            {t("billing.dashboard.pending_section_title")}
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            {t("billing.dashboard.pending_section_blurb")}
          </p>
          <ul className="mt-5 space-y-3">
            {pendingReview.slice(0, 6).map((inv) => (
              <li
                key={inv.id}
                data-testid={`pending-review-row-${inv.id}`}
                className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#F4E5C5] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[#1A1B2E]">{inv.invoice_number}</div>
                  <div className="text-xs text-[#6B7280]">
                    {inv.parent_name} · {inv.child_name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-base text-[#1A1B2E] tabular-nums">
                    {money(inv.amount_due, inv.currency)}
                  </div>
                  <Link
                    to={`/admin/billing/invoices/${inv.id}`}
                    className="text-xs text-[#1F3A2A] hover:underline"
                    data-testid={`pending-review-link-${inv.id}`}
                  >
                    {t("billing.dashboard.review_payment_cta")}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {overdue.length > 0 && (
        <section
          className="rounded-3xl bg-[#FCEEEE] border border-[#FCD3D3] p-6 lg:p-8"
          data-testid="billing-overdue-section"
        >
          <h3 className="font-display text-xl text-[#A11D1D]">
            {t("billing.dashboard.overdue_section_title")}
          </h3>
          <ul className="mt-5 space-y-3">
            {overdue.slice(0, 6).map((inv) => (
              <li
                key={inv.id}
                data-testid={`overdue-row-${inv.id}`}
                className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#FCD3D3] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[#1A1B2E]">{inv.invoice_number}</div>
                  <div className="text-xs text-[#6B7280]">
                    {inv.parent_name} · {inv.child_name}
                  </div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div className="font-display text-base text-[#1A1B2E] tabular-nums">
                    {money(inv.amount_due, inv.currency)}
                  </div>
                  <Link
                    to={`/admin/billing/invoices/${inv.id}`}
                    className="text-xs text-[#1F3A2A] hover:underline"
                    data-testid={`overdue-link-${inv.id}`}
                  >
                    {t("billing.dashboard.open_invoice_cta")}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}

export { StatusPill, money };
